import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import {
    SewButton,
    SewCheckbox,
    SewCheckboxGroup,
    SewCombobox,
    SewFileUpload,
    SewForm,
    SewFormRow,
    SewInput,
    SewMarkdown,
    SewRadio,
    SewRadioGroup,
    SewRichtext,
    SewRichtextEditor,
    SewWizard,
    SewWizardItem,
    SewWsButton,
    SewWsRichtext,
} from '@sew/ui-components-angular/standalone';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { NgUseDirective } from '../../directives/ng-use/ng-use.directive';
import { OnDestroyBase } from '../../helpers/public-helpers';
import { SafeHtmlPipe } from '../../pipes/safe-html/safe-html.pipe';
import { User } from '../../services/public-services';
import {
    getIDPathByFormElementName,
    isConditionArray,
    isConditionElement,
    isConditionalFieldsArray,
    isFormElement,
    isPageBreakElement,
} from './caas-monday-webforms.helper';
import {
    Condition,
    ConditionConnection,
    ConditionalFields,
    ElementOption,
    FormElement,
    FormPage,
    MwfElementTypes,
    Property,
    Validator,
    WebFormResponse,
    WebformsAction,
} from './caas-monday-webforms.interface';
import { CaasMondayWebformsService } from './caas-monday-webforms.service';
import { SewRichtTextDirective } from './form-controls/sew-richt-text.directive';
import { MondayWebformsFormControl } from './helper/monday-webforms-form-control';
import { dateUpToFutureDays, dateUpToPastDays, fileTypeMapper } from './public-api-caas-monday-webforms';
import {
    CheckBoxElement,
    ContextFormElement,
    ContextFormElementStyle,
    DropDownOption,
    FormSubmitEvent,
    RadioButtonElement,
    RowFormData,
    StepFormData,
} from './web-formular.interface';
@Component({
    selector: 'osc-caas-monday-webforms',
    templateUrl: './caas-monday-webforms.component.html',
    styleUrls: ['./caas-monday-webforms.component.scss'],
    imports: [
        NgUseDirective,
        ReactiveFormsModule,
        SewRichtTextDirective,
        TranslatePipe,
        SafeHtmlPipe,
        SewButton,
        SewCheckbox,
        SewCheckboxGroup,
        SewCombobox,
        SewFileUpload,
        SewForm,
        SewFormRow,
        SewInput,
        SewMarkdown,
        SewRadio,
        SewRadioGroup,
        SewRichtext,
        SewRichtextEditor,
        SewWizard,
        SewWizardItem,
        SewWsButton,
        SewWsRichtext,
    ],
})
export class CaasMondayWebformsComponent extends OnDestroyBase implements OnInit, OnDestroy {
    private _user: User | null | undefined;

    @Output() public submitForm = new EventEmitter<FormSubmitEvent>();
    @Output() public description = new EventEmitter<string>();
    @Output() public successMessage = new EventEmitter<string>();
    @Output() public title = new EventEmitter<string>();
    @Output() public mac = new EventEmitter<string>();
    @Output() public actionChange = new EventEmitter<WebformsAction | null>();

    @Input() public formularPath?: string;
    @Input() public queryParamToFormElementMapping: { [key: string]: string } = {};
    @Input() public datepickerConfig: any = {};
    @Input() public ws = false;
    @Input() public darkTheme = false;
    @Input() public buttonFullWidth = false;
    @Input() public noMargin = false;
    @Input() public noGrid = false;
    @Input() public formValidators: ValidatorFn[] = [];
    @Input() set user(value: User | null | undefined) {
        this._user = value;
        // Rebuild the form when the user loggs in
        this.rebuildForm();
    }

    @Input() public createFormControl: (
        formElement: ContextFormElement,
        value: string | null,
        caasMondayWebforms: CaasMondayWebformsComponent,
        user: User | null | undefined
    ) => UntypedFormControl;

    @Input() public isVisibleByDefault: (elementName: string) => boolean;

    public form: UntypedFormGroup = this.fb.group({});
    public formElementsContext: StepFormData[] = [];
    public displayErrorDialog = false;
    public stepperIndex = 1;
    public fileList?: File[] | null;
    public captchaImage?: SafeUrl;
    public allowedMimeTypes?: (string | null)[] | undefined;

    private captchaId?: string | null;
    private maxFileSize = 0;

    public constructor(
        private fb: UntypedFormBuilder,
        private route: ActivatedRoute,
        private caasMondayWebformsService: CaasMondayWebformsService
    ) {
        super();
        this.createFormControl = (formElement: ContextFormElement, value: string | null) =>
            new MondayWebformsFormControl(value, formElement.validators, undefined, formElement.label);
        this.isVisibleByDefault = () => true;
    }

    public ngOnInit() {
        if (!this.formularPath) {
            return;
        }

        this.fetchData(this.formularPath, this.createFormControl);

        this.caasMondayWebformsService.captchaData
            .pipe(takeUntil(this.destroySubject))
            .subscribe((result) => {
                this.captchaId = result.captchaId;
                this.captchaImage = result.captchaImage;
                this.form.get(getIDPathByFormElementName('captcha', this.form))?.setValue('');
            });
    }

    get user() {
        return this._user;
    }

    private rebuildForm() {
        if (this.formularPath && this.createFormControl) {
            this.fetchData(this.formularPath, this.createFormControl);
        }
    }

    public onSubmit() {
        this.submitForm.emit({ form: this.form, captchaId: this.captchaId, fileList: this.fileList });
    }

    public isElementInvalid(step: string, element: string) {
        const control = this.form.get(step)?.get(element);

        return !!control?.invalid && (control.dirty || control.touched);
    }

    public isAutocompleteOff(step: string, element: string) {
        const control = this.form.get(step)?.get(element);

        return !!control && control instanceof MondayWebformsFormControl && control.isAutocompleteOff;
    }

    public getCaptcha() {
        this.caasMondayWebformsService.getCaptcha(Math.floor(Math.random() * 9999999) + 1);
    }

    public isStepInvalid(step: string) {
        const controlGroup = this.form.get(step);

        return !!controlGroup?.invalid;
    }

    public setControlsInStepTouched(step: string) {
        this.form.updateValueAndValidity();
        this.form.get(step)?.markAllAsTouched();
    }

    public updateFileList(event: Event) {
        const customEvent = event as CustomEvent;
        const files = Array.from(customEvent.detail as FileList);

        const acceptedFiles = files.filter(
            (file) => this.allowedMimeTypes?.includes(file.type) && file.size < this.maxFileSize
        );

        this.fileList = acceptedFiles;
    }

    public addRequiredValidator(elementPaths: string[]) {
        elementPaths.forEach((path) => {
            this.form.get(path)?.addValidators(Validators.required);
        });
    }

    public removeRequiredValidator(elementPaths: string[]) {
        elementPaths.forEach((path) => {
            this.form.get(path)?.removeValidators(Validators.required);
        });
    }

    public updateFormValidity(elementPaths: string[]) {
        elementPaths.forEach((path) => {
            this.form.get(path)?.updateValueAndValidity();
        });
    }

    public setElementVisibility(elementNames: string[], visibility?: boolean) {
        this.formElementsContext.map((step) =>
            step.rows.map((row) => {
                row.elements.map((element) => {
                    if (elementNames.includes(element.name)) {
                        element.visible = visibility !== undefined ? visibility : !element.visible;
                    }
                });
            })
        );
    }

    public getElementVisibility(elementName: string) {
        const match = this.formElementsContext
            .flatMap((item) => item.rows)
            .flatMap((row) => row.elements)
            .find((element) => element.name === elementName);

        return match?.visible;
    }

    private fetchData(
        formularPath: string,
        createFormControl: (
            formElement: ContextFormElement,
            value: any | null,
            caasMondayWebforms: CaasMondayWebformsComponent,
            user?: User | null | undefined
        ) => UntypedFormControl
    ) {
        this.caasMondayWebformsService
            .fetchWebFormsData(formularPath)
            .pipe(
                filter((response): response is WebFormResponse => response !== null),
                filter((response) => response._embedded !== undefined),
                map((response) => {
                    const formPage = response._embedded['rh:doc'][0];
                    this.emitValues(formPage);
                    const conditions: ConditionConnection[] = [];

                    const formElementsContext = formPage.formular.form.element.reduce((previous, element) => {
                        if (isPageBreakElement(element)) {
                            const stepper: StepFormData = {
                                name: element.label,
                                id: element['xml:id'],
                                nextText: this.getButtonTexts(
                                    Array.isArray(element.property)
                                        ? element.property?.find((prop) => prop.name === 'next_label')
                                        : element.property,
                                    'next_label'
                                ),
                                backText: this.getButtonTexts(
                                    Array.isArray(element.property)
                                        ? element.property?.find((prop) => prop.name === 'back_label')
                                        : element.property,
                                    'back_label'
                                ),
                                rows: [],
                            };
                            previous.push(stepper);
                        } else if (isConditionElement(element)) {
                            if (Array.isArray(element.property)) {
                                let elementConditions: Condition[];
                                let affectedFields: ConditionalFields[];
                                try {
                                    // Monday Webforms is returning the input array ase a string. So it will be parsed to an object ehre
                                    elementConditions = JSON.parse(
                                        element.property.find((property) => property.name === 'condition')
                                            ?.value as any
                                    );
                                    affectedFields = JSON.parse(
                                        element.property.find(
                                            (property) => property.name === 'conditional_fields'
                                        )?.value as any
                                    );
                                } catch (error) {
                                    return previous;
                                }

                                const conjunction = element.property.find(
                                    (property) => property.name === 'condition_conjunction'
                                )?.value;

                                if (
                                    !elementConditions ||
                                    !isConditionArray(elementConditions) ||
                                    !affectedFields ||
                                    !isConditionalFieldsArray(affectedFields) ||
                                    !conjunction ||
                                    typeof conjunction !== 'boolean'
                                ) {
                                    return previous;
                                }

                                const condition: ConditionConnection = {
                                    conditions: elementConditions.map((elementCondition) => ({
                                        fieldId: elementCondition.input,
                                        operator: elementCondition.operator,
                                        value: elementCondition.value,
                                    })),
                                    affectedFields: affectedFields.map((affectedField) => ({
                                        fieldId: affectedField.input,
                                        state: affectedField.state,
                                    })),
                                    conjunction,
                                };

                                conditions.push(condition);
                            }
                        } else if (isFormElement(element)) {
                            const sewElement = this.createFormElementContextObject(element, this.user);
                            // for single page forms it is ok to omit the pageBreak in the form definition
                            // in that case we create a StepFormData and use the submitButton of the form instead
                            if (previous.length === 0) {
                                const stepper: StepFormData = {
                                    name: null,
                                    id: element['xml:id'],
                                    nextText: this.getButtonTexts(
                                        Array.isArray(formPage.formular.form.property)
                                            ? formPage.formular.form.property?.find(
                                                  (prop) => prop.name === 'submit_label'
                                              )
                                            : formPage.formular.form.property,
                                        'submit_label'
                                    ),
                                    backText: '',
                                    rows: [],
                                };
                                previous.push(stepper);
                            }

                            const currentStep = previous[previous.length - 1];
                            const currentRow =
                                currentStep.rows.length > 0 && currentStep.rows[currentStep.rows.length - 1];

                            if (currentRow) {
                                if (this.isRowSpaceExceeded(currentRow, sewElement)) {
                                    const row: RowFormData = {
                                        elements: [sewElement],
                                        fill: sewElement.style.fill,
                                    };
                                    currentStep.rows.push(row);
                                } else {
                                    currentRow.elements.push(sewElement);
                                }
                            } else {
                                const row: RowFormData = {
                                    elements: [sewElement],
                                    fill: sewElement.style.fill,
                                };
                                currentStep.rows.push(row);
                            }
                        }

                        return previous;
                    }, [] as StepFormData[]);

                    const action = formPage.formular.form.action;

                    return { formElementsContext, conditions, action };
                }),
                tap(({ formElementsContext }) => {
                    /**
                     * Anhand der Context-Informationen der FormElemente muss jetzt die FormGroup für html zusammengebaut werden:
                     */
                    this.form = this.fb.group(
                        formElementsContext.reduce(
                            (result, step) => ({
                                ...result,
                                [step.id]: this.fb.group(
                                    step.rows.reduce(
                                        (currentRow, row) =>
                                            row.elements.reduce(
                                                (group, formElement) => ({
                                                    ...group,
                                                    [formElement.name]: createFormControl(
                                                        formElement,
                                                        this.user ? formElement.defaultValue : null,
                                                        this,
                                                        this.user ?? null
                                                    ),
                                                }),
                                                currentRow
                                            ),
                                        {}
                                    ),
                                    { validators: this.formValidators }
                                ),
                            }),
                            {}
                        )
                    );
                })
            )
            .subscribe(({ formElementsContext, conditions, action }) => {
                this.formElementsContext = formElementsContext;
                this.setConditions(conditions);
                this.setQueryParamValues();
                this.actionChange.emit(action ?? null);
            });
    }

    /**
     * Sets the conditions of the form
     *
     * Call this function only after formElementsContext is set, cause it is used in some functions.
     *
     * @param conditions
     */
    private setConditions(conditions: ConditionConnection[]) {
        conditions.forEach((condition) => {
            if (condition.conditions.length > 1) {
                // TODO: use the condition.conjunction to combine the multiple conditions
            } else {
                const con = condition.conditions[0];
                const element = this.form.get(getIDPathByFormElementName(con.fieldId, this.form));

                if (!element) {
                    return;
                }

                element.valueChanges.subscribe((change: any) => {
                    switch (con.operator) {
                        case 'selected':
                            if (change.toString() === con.value) {
                                condition.affectedFields.forEach((affectedField) => {
                                    switch (affectedField.state) {
                                        case 'visible':
                                            this.setElementVisibility([affectedField.fieldId], true);
                                            break;
                                        case 'hidden':
                                            this.setElementVisibility([affectedField.fieldId], false);
                                            break;

                                        default:
                                            break;
                                    }
                                });
                            } else {
                                condition.affectedFields.forEach((affectedField) => {
                                    switch (affectedField.state) {
                                        case 'visible':
                                            this.setElementVisibility([affectedField.fieldId], false);
                                            break;
                                        case 'hidden':
                                            this.setElementVisibility([affectedField.fieldId], true);
                                            break;

                                        default:
                                            break;
                                    }
                                });
                            }
                            break;

                        default:
                            break;
                    }
                });
            }
        });
    }

    private isRowSpaceExceeded(row: RowFormData, nextElement: ContextFormElement): boolean {
        if (row.elements[row.elements.length - 1].style.fill) {
            return true;
        }

        let rowSpace = 0;
        row.elements.forEach((element) => {
            rowSpace += element.style.width;
        });

        return (rowSpace += nextElement.style.width) > 100;
    }

    private getDefaultValue(elementName: string, user: User) {
        switch (elementName) {
            case 'contactFirstName':
                return user.firstname;
            case 'contactLastName':
                return user.lastname;
            case 'email':
                return user.emailMain;
            case 'phoneNumber':
                return user.relations.find((relation) => relation.default === 'X')?.telNum ?? '';
            case 'customer':
                return user.relations.find((relation) => relation.default === 'X')?.cusName ?? '';
            case 'postcode':
                return user.relations.find((relation) => relation.default === 'X')?.pstcd ?? '';
            case 'country_code':
                return user.homeCountry;
            case 'salutation':
                return user.salutation;
            default:
                return '';
        }
    }

    private createFormElementContextObject(
        element: FormElement,
        user: User | null | undefined
    ): ContextFormElement {
        //TODO: wenn der User mehrere Beziehungen hat, dann muss company z.B. zu einer Dropdown werden und phoneNumber einen neuen Wert haben!
        switch (element.type) {
            case MwfElementTypes.COMBO_BOX:
                return {
                    id: element['xml:id'],
                    label: element.label,
                    name: element.name,
                    type: element.type,
                    options: this.getDropDownOptions(element.element),
                    style: this.getFormElementStyle(
                        Array.isArray(element.property)
                            ? element.property?.find((prop) => prop.name === 'style_class')
                            : element.property
                    ),
                    defaultValue: user ? this.getDefaultValue(element.name, user) : '',
                    errorMsg: this.getErrorMessage(
                        Array.isArray(element.property)
                            ? element.property?.find((prop) => prop.name === 'hint')
                            : element.property
                    ),
                    visible: this.isVisibleByDefault(element.name),
                    validators: this.createFormValidators(element.validator),
                };

            case MwfElementTypes.FILE_UPLOAD:
                const maxFileSize = ((element.validator as Validator).property as Property[]).find(
                    (fileSize: Property) => fileSize.name === 'max_size'
                )?.value;

                const cmsFileTypes = ((element.validator as Validator).property as Property[])
                    .find((fileTypes: Property) => fileTypes.name === 'file_types')
                    ?.value.toString()
                    .replace(/ /g, '')
                    .split(',');

                const acceptedFileTypes = cmsFileTypes?.map((fileType) => `.${fileType.replace(/ /g, '')}`);

                this.allowedMimeTypes = cmsFileTypes
                    ?.map((fileType) => fileTypeMapper(fileType))
                    .filter((mimeType) => mimeType !== null);
                this.maxFileSize = Number(maxFileSize) * 1000; //Convert Byte in KByte

                return {
                    id: element['xml:id'],
                    label: element.label,
                    name: element.name,
                    type: this.createElementType(element.type, element.validator, element.property),
                    style: { width: 100, fill: false },
                    defaultValue: user ? this.getDefaultValue(element.name, user) : '',
                    validators: this.createFormValidators(element.validator),
                    visible: this.isVisibleByDefault(element.name),
                    acceptedFileTypes: acceptedFileTypes?.join(','),
                    maxFileSize: this.maxFileSize.toString(),
                    errorMsg: this.getErrorMessage(
                        Array.isArray(element.property)
                            ? element.property?.find((prop) => prop.name === 'hint')
                            : element.property
                    ),
                };
            case MwfElementTypes.CAPTCHA:
                this.getCaptcha();

                return {
                    id: element['xml:id'],
                    label: element.label,
                    name: element.name,
                    type: this.createElementType(element.type, element.validator, element.property),
                    style: { width: 50, fill: true },
                    defaultValue: user ? this.getDefaultValue(element.name, user) : '',
                    validators: this.createFormValidators({
                        'xml:id': 'captcha1907',
                        name: 'notempty',
                        property: {} as Property,
                    }),
                    visible: this.isVisibleByDefault(element.name),
                    errorMsg: this.getErrorMessage(
                        Array.isArray(element.property)
                            ? element.property?.find((prop) => prop.name === 'hint')
                            : element.property
                    ),
                };

            case MwfElementTypes.RADIO_GROUP:
                return {
                    id: element['xml:id'],
                    label: element.label,
                    name: element.name,
                    type: element.type,
                    elements: this.getRadioButtonElements(element.element),
                    style: this.getFormElementStyle(
                        Array.isArray(element.property)
                            ? element.property?.find((prop) => prop.name === 'style_class')
                            : element.property
                    ),
                    defaultValue: user ? this.getDefaultValue(element.name, user) : '',
                    errorMsg: this.getErrorMessage(
                        Array.isArray(element.property)
                            ? element.property?.find((prop) => prop.name === 'hint')
                            : element.property
                    ),
                    visible: this.isVisibleByDefault(element.name),
                    validators: this.createFormValidators(element.validator),
                };

            case MwfElementTypes.PARAGRAPH:
                return {
                    id: element['xml:id'],
                    label: element.value,
                    name: element.name,
                    type: element.type,
                    style: this.getFormElementStyle(
                        Array.isArray(element.property)
                            ? element.property?.find((prop) => prop.name === 'style_class')
                            : element.property
                    ),
                    visible: this.isVisibleByDefault(element.name),
                    defaultValue: '',
                    errorMsg: null,
                };

            case MwfElementTypes.CHECK_BOX_GROUP:
                const elements: ElementOption[] = Array.isArray(element.element)
                    ? element.element
                    : [element.element];

                return {
                    id: element['xml:id'],
                    label: element.label,
                    name: element.name,
                    elements: this.getCheckBoxElements(elements),
                    type: this.createElementType(element.type, element.validator, element.property),
                    style: this.getFormElementStyle(
                        Array.isArray(element.property)
                            ? element.property?.find((prop) => prop.name === 'style_class')
                            : element.property
                    ),
                    defaultValue: user ? this.getDefaultValue(element.name, user) : '',
                    validators: this.createFormValidators(element.validator),
                    visible: this.isVisibleByDefault(element.name),
                    errorMsg: this.getErrorMessage(
                        Array.isArray(element.property)
                            ? element.property?.find((prop) => prop.name === 'hint')
                            : element.property
                    ),
                };

            default:
                return {
                    id: element['xml:id'],
                    label: element.label,
                    name: element.name,
                    type: this.createElementType(element.type, element.validator, element.property),
                    style: this.getFormElementStyle(
                        Array.isArray(element.property)
                            ? element.property?.find((prop) => prop.name === 'style_class')
                            : element.property
                    ),
                    defaultValue: user ? this.getDefaultValue(element.name, user) : '',
                    validators: this.createFormValidators(element.validator),
                    visible: this.isVisibleByDefault(element.name),
                    errorMsg: this.getErrorMessage(
                        Array.isArray(element.property)
                            ? element.property?.find((prop) => prop.name === 'hint')
                            : element.property
                    ),
                };
        }
    }

    private getFormElementStyle(styleProperty: Property | undefined): ContextFormElementStyle {
        //Default, when no style-property was found
        const style: ContextFormElementStyle = { width: 100, fill: false };

        if (styleProperty && styleProperty.name === 'style_class') {
            style.fill = styleProperty.value.toString().includes('fill') ? true : false;
            const width = styleProperty.value.toString().match(/\d+/g);
            style.width = width && width[0] ? Number(width[0]) : 100;
        }

        return style;
    }

    private getButtonTexts(property: Property | undefined, searchValue: string) {
        return property && property?.name === searchValue ? (property.value as string) : '';
    }

    private getErrorMessage(property: Property | undefined) {
        return property && property.name === 'hint' ? (property.value as string) : '';
    }

    private createElementType(
        type: string,
        validator: Validator | Validator[],
        properties: Property | Property[]
    ) {
        const datePicker = Array.isArray(validator)
            ? validator.find((prop) => prop.name === 'date')
            : validator?.name === 'date'
              ? validator
              : null;

        const richTextEditor = Array.isArray(properties)
            ? properties.find((prop) => prop?.value === 'rich-text')
            : properties?.value === 'rich-text'
              ? validator
              : null;

        if (datePicker) {
            return 'date';
        } else if (richTextEditor) {
            return 'richTextEditor';
        } else {
            return type;
        }
    }

    private createFormValidators(validators: Validator[] | Validator) {
        const ngValidators = [];

        if (validators) {
            if (Array.isArray(validators)) {
                validators.forEach((validator) => {
                    const validatorFunction = this.getNgFormValidators(validator.name, validator.property);
                    ngValidators.push(...validatorFunction);
                });
            } else {
                const validatorFunction = this.getNgFormValidators(validators.name, validators.property);
                ngValidators.push(...validatorFunction);
            }
        }

        return ngValidators;
    }

    private getNgFormValidators(cmsValidator: string, validatorProps?: Property | Property[]): ValidatorFn[] {
        const validators: ValidatorFn[] = [];

        if (Array.isArray(validatorProps)) {
            validatorProps.forEach((validatorProp) => {
                const validator = this.getNgFormValidator(cmsValidator, validatorProp);
                if (validator !== null) {
                    validators.push(validator);
                }
            });
        } else {
            const validator = this.getNgFormValidator(cmsValidator, validatorProps);
            if (validator !== null) {
                validators.push(validator);
            }
        }

        return validators;
    }

    private getNgFormValidator(cmsValidator: string, validatorProps?: Property): ValidatorFn | null {
        let validator: string | undefined = cmsValidator;
        let days = 0;

        if (cmsValidator === 'date') {
            validator = validatorProps?.name;
            if (validatorProps?.value) {
                days = validatorProps.value as number;
            }
        }

        switch (validator) {
            case 'notempty':
                return Validators.required;
            case 'number':
                return Validators.pattern('[0-9]*');
            case 'phone':
                return Validators.pattern('[- +()0-9/]+');
            case 'email':
                return Validators.email;
            case 'days_from':
                return dateUpToPastDays(days);
            case 'days_to':
                return dateUpToFutureDays(days);
            case 'file':
            default:
                return null;
        }
    }

    private getDropDownOptions(childElements: ElementOption[]): DropDownOption[] {
        return childElements.map((option) => ({ name: option.label, value: option.value }));
    }

    private getRadioButtonElements(childElements: ElementOption[]): RadioButtonElement[] {
        return childElements.map((element) => ({
            label: element.label,
            value: element.value,
            id: element['xml:id'],
        }));
    }

    private getCheckBoxElements(childElements: ElementOption[]): CheckBoxElement[] {
        return childElements.map((element) => ({
            label: element.label,
            value: element.value,
            id: element['xml:id'],
        }));
    }

    private setQueryParamValues(): void {
        this.route.queryParams.subscribe((params) => {
            Object.keys(this.queryParamToFormElementMapping).forEach((key) => {
                const value = params[key];
                if (value) {
                    this.setFormElementValue(this.queryParamToFormElementMapping[key], value);
                }
            });
        });
    }

    private setFormElementValue(name: string, value: string) {
        Object.keys(this.form.value).forEach((groupKey) => {
            Object.keys(this.form.value[groupKey]).forEach((key) => {
                if (name === key) {
                    this.form.get([groupKey, name])?.setValue(value);
                    this.form.get([groupKey, name])?.markAsTouched();
                }
            });
        });
    }

    private emitValues(formPage: FormPage) {
        this.description.emit(formPage.description);
        this.successMessage.emit(formPage.success_message);
        this.title.emit(formPage.title);
        if (formPage.mac) {
            this.mac.emit(formPage.mac);
        }
    }
}
